import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {LocalizedText} from '../api/types';
import {en} from './en';
import {
  BCP47,
  DEFAULT_LANGUAGE,
  LANGUAGES,
  type Language,
  type MessageKey,
  type MessageParams,
  type Messages,
} from './keys';
import {
  ZONE_UNKNOWN,
  formatSatang,
  formatSignedSatang,
  formatTime,
  interpolate,
  localized,
} from './format';
import {th} from './th';
import {zh} from './zh';

/**
 * The whole i18n runtime, in one module and with no dependency.
 *
 * All three dictionaries are in the initial bundle rather than lazily fetched.
 * That is deliberate: a tourist switching to Chinese is doing it *because* the
 * page is unreadable, and making that switch wait on a round trip over 4G is the
 * worst possible moment to spend the perf budget. Three dictionaries of plain
 * strings compress to very little; a language that arrives late does not.
 */

const DICTIONARIES: Record<Language, Messages> = {th, en, zh};

export interface I18nValue {
  language: Language;
  setLanguage: (language: Language) => void;
  /** All three, in a stable order, for the toggle. */
  languages: readonly Language[];
  /** Translate. `{braces}` are filled from `params`. */
  t: (key: MessageKey, params?: MessageParams) => string;
  /** Read a `LocalizedText` from the API in the current language, Thai as fallback. */
  ln: (text: LocalizedText | null | undefined) => string;
  /** Integer satang → `฿1,250.50`. Satang only when non-zero. */
  money: (satang: number) => string;
  /**
   * A signed price **difference** → `+฿10`, `−฿5`, `฿0`.
   *
   * On the context beside `money` rather than imported directly, so a screen
   * rendering a modifier delta never reaches for `money()` and prefixes a sign
   * by hand — which is how the design prototype ended up printing `฿-5`.
   */
  signedMoney: (satang: number) => string;
  /**
   * ISO timestamp → 24-hour wall clock **in the venue's zone**.
   *
   * Empty until the zone has arrived. See `setTimeZone` and `ZONE_UNKNOWN`.
   */
  time: (iso: string) => string;
  /**
   * Tell the surface which zone the venue trades in.
   *
   * Called by `useServiceStatus` when `ServiceStatus.time_zone` lands (C-4,
   * D-023). It lives here rather than on the session because `I18nProvider` sits
   * ABOVE `GuestSessionProvider` — provider order is a dependency chain and
   * inverting it to read a session would be a much larger change than a setter.
   */
  setTimeZone: (zone: string) => void;
  /** BCP 47 tag for `preferred_language` and `<html lang>`. */
  bcp47: string;
}

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'pokpok.web.language';

/**
 * The language a guest arrives in.
 *
 * Order: a language they already chose on this phone, then the phone's own
 * language, then Thai. **Thai is the floor, never a guess** — foundations §8
 * makes Thai the design language, and an unrecognised browser locale is not
 * evidence of anything.
 */
export function detectLanguage(
  stored: string | null,
  // Kept in the signature for the callers that still pass it, but no longer
  // read: the page does not follow the phone's language.
  _navigatorLanguages: readonly string[] = [],
): Language {
  // **A guest choice, then Thai — never the phone's language.** The menu is a
  // Thai restaurant's and the copy is written Thai-first; a tourist whose phone
  // is set to English used to land on an English shell over Thai dish names,
  // which is worse than Thai throughout. So the page opens in Thai unless the
  // guest has explicitly picked another language on it before (owner,
  // 2026-08-17), and the language switcher is how they change it — the phone's
  // `navigator.languages` is not consulted at all.
  if (isLanguage(stored)) return stored;
  return DEFAULT_LANGUAGE;
}

function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

function readStored(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    // Private browsing, or a locked-down WebView. Not an error worth surfacing.
    return null;
  }
}

export function I18nProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  /** Tests and the scan-landing deep link pass this; otherwise it is detected. */
  initialLanguage?: Language;
}) {
  const [timeZone, setTimeZone] = useState<string>(ZONE_UNKNOWN);
  const [language, setLanguageState] = useState<Language>(
    () =>
      initialLanguage ??
      detectLanguage(readStored(), globalThis.navigator?.languages ?? []),
  );

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, next);
    } catch {
      // Choosing a language must work even when nothing can be written down.
    }
  }, []);

  // Thai wraps and hyphenates differently from Latin text and Chinese line-breaks
  // differently again; `lang` is what lets the browser do the right one.
  useEffect(() => {
    globalThis.document?.documentElement.setAttribute('lang', BCP47[language]);
  }, [language]);

  const value = useMemo<I18nValue>(() => {
    const dictionary = DICTIONARIES[language];
    return {
      language,
      setLanguage,
      languages: LANGUAGES,
      t: (key, params) => interpolate(dictionary[key], params),
      ln: (text) => localized(text, language),
      money: formatSatang,
      signedMoney: formatSignedSatang,
      time: (iso) => formatTime(iso, language, timeZone),
      setTimeZone,
      bcp47: BCP47[language],
    };
  }, [language, setLanguage, timeZone]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * The hook every screen uses.
 *
 * Throws outside the provider rather than falling back to Thai, because a silent
 * fallback would ship a screen that looks fine in review and is unswitchable in
 * a tourist venue.
 */
export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}
